import axios, { AxiosResponse } from "axios";
import { DeparturesOptions, LineInfos, StopPoint, Destination, TimeData, mergeTimes, simulateOccupancy } from "./types";
import { Line } from './types/line';
import { GtfsSchedule } from "./gtfsTypes";
import getComputedTimes from "./getComputedTimes";

var dictLines = {};

function getLineInfos(  
  gtfsUri: string,
  network: string,
  lineId): Promise<LineInfos> {
  if (lineId in dictLines){
    return new Promise( (resolve,reject) => {
       resolve(dictLines[lineId]);
    });
  }

  let url = `${gtfsUri}/line?reseau=${network}&line=${lineId}`;
  return axios.request<LineInfos>({
    method: "get",
    url: url,
    transformResponse: (resp: string) => {
      let data = JSON.parse(resp);
      return new LineInfos( data["Dir0Label"], data["Dir1Label"]);
    }
  }).then(res => {
      dictLines[lineId] = res.data;
      return res.data;
    });
}

async function getDepartures(
  lines: { [key: string]: Line },
  busStop: StopPoint,
  schedules: GtfsSchedule[],
  gtfsUri: string,
  network: string
): Promise<Destination[]> {
  let destinations: Destination[] = [];
  let lineIds = Object.keys(lines);

  for (let i in schedules) {
    let schedule = schedules[i];
    // Dont get departures for schedules if they are not associated with one of the "lines" parameter
    if (!lineIds.includes(schedule.RouteId)) {
      continue;
    }
    let times = getComputedTimes(schedule);
    if (times.length > 0) {
      //Get the real ref by mnemo on busStop
      let serviceId = schedule.RouteId;
      let fromNames = Object.values(schedule.Schedule).map(timeSched => timeSched.from_stop_name); 
      let fromlib = Array.from(new Set(fromNames)).join("/"); //unique values, separated by "/";
      let line = lines[serviceId];
      if (serviceId in lines){
        let lineInfos = await getLineInfos(gtfsUri, network, lines[serviceId].ref);
        line["infos"] = lineInfos;
      }
      let destination: Destination = {
        direction: <string><unknown>schedule.Direction,
        dirlib: schedule.DirLib,
        fromlib: fromlib,
        mnemoService: schedule.ShortName,
        serviceId,
        firstTime: times[0],
        times,
        line
      };

      if (schedule.serviceDisruption) {
        destination.serviceDisruption = schedule.serviceDisruption;
      }
      if (schedule.globalDisruption) {
        destination.globalDisruption = schedule.globalDisruption;
      }
      if (schedule.busStopDisruption) {
        destination.busStopDisruption = schedule.busStopDisruption;
      }
      destinations.push(destination);
    }
  }

  //Reconstruction of dests/lines for lines with no departures informations
  // if (cObj.reconstructTimesByDestInfo) {
  for (let i in busStop.dests) {
    let dest = busStop.dests[i]; // get from global object Dests
    if (dest) {
      let hasAlreadyTheLine = false;
      for (let j in destinations) {
        let destination = destinations[j];
        if (
          destination.serviceId == dest.service ||
          destination.mnemoService == dest.service
        ) {
          hasAlreadyTheLine = true;
        }
      }

      if (!hasAlreadyTheLine && dest.service) {
        let line = lines[dest.service];
        destinations.push({
          firstTime: new TimeData(false, dest.name, false),
          line: lines[line.ref],
          mnemoService: line.mnemo,
          serviceId: line.ref,
          times: []
        });
      }
    }
  }
  return destinations;
}

function gtfsStopDepartures(
  spoint: StopPoint,
  lines: { [key: string]: Line },
  gtfsUri: string,
  encodeUriPath: Boolean,
  network: string,
  options: DeparturesOptions
): Promise<AxiosResponse<Destination[]>> {
  // date?: string,
  // time?: string,
  // limit?: number,
  // nbpasttimes?: number,
  // pastsince?: number,

  let nbtimes = 2; // default
  if (options.limit){
    nbtimes = options.limit;
  }
  let stopUri = `/stop-stoptimes-discovery?nbtimes=${nbtimes}&stop=${spoint.ref}&reseau=${network}`;
  if (options.date) {
    stopUri += `&date=${options.date}`;
  }
  if (options.time) {
    stopUri += `&heure=${options.time}`;
  }
  if (encodeUriPath) {
    stopUri = encodeURIComponent(stopUri);
  }
  if (options.nbpasttimes !== undefined){
    if (!options.pastsince){
      options.pastsince = 480; // 8h 
    }
    stopUri += `&nbpasttimes=${options.nbpasttimes}&pastsince=${options.pastsince}`;
  }
  stopUri = gtfsUri + stopUri;
  // let stopUri = `${gtfsUri}api/stop-stoptimes-discovery?nbtimes=2&stop=${spoint.ref}&reseau=${network}`;
  return axios.request<Destination[]>({
    method: "get",
    url: stopUri,
    transformResponse: (resp: string) => {
      let data = JSON.parse(resp);
      let resTimes = data["items"]
      return getDepartures(lines, spoint, Object.values(resTimes), gtfsUri, network);
    }
  });
}

export default function(
  gtfsUri: string,
  encodeUriPath: Boolean,
  network: string,
  stopsPoints: StopPoint[],
  lines: { [key: string]: Line },
  options: DeparturesOptions
): Promise<Destination[]> {
  let stops: StopPoint[] = [];
  for (let stop of stopsPoints) {
    stops = stops.concat(stop.spoints.length ? stop.spoints : [stop]);
  }
  if (!options.limit){
    options.limit = 2;
  }

  if (!options.simulateOccupancy) {
    options.simulateOccupancy = false;
  }

  let destsPromises = stops.flatMap(spoint => {
    return gtfsStopDepartures(spoint, lines, gtfsUri, encodeUriPath, network, options);
  });

  return axios.all(destsPromises).then(results => {
    // let rawDestinationsProm: Promise<Destination[]> = results.map(res => res.data).flat();
    let rawDestinationsProm: Destination[] = results.map(res => res.data).flat();
    // let rawDestinationsProm: any = results.map(res => res.data).flat();
    return Promise.all(rawDestinationsProm).then(rawDestinations => {
      //Merge
      let iniDict: { [key: string]: Destination } = {};
      let mergedDests: { [key: string]: Destination } = rawDestinations.flat().reduce(
        (acc, curr) => {
          let strCurr = curr.mnemoService + " " + curr.firstTime.nameDest;
          
          if (acc.hasOwnProperty(strCurr)) {
            acc[strCurr].times = mergeTimes(acc[strCurr].times, curr.times).slice(0, options.limit);
          } else {
            if (!curr.line){
              curr.line = null;
            }
            acc[strCurr] = curr;
            acc[strCurr].line.refs = [];
          }
          // Add fake data for occupancy if simulateOccupancy option is TRUE
          if (acc[strCurr].times && options.simulateOccupancy) {
            simulateOccupancy(acc[strCurr].times);
          }
          if (curr.line && curr.line.ref){
            acc[strCurr].line.refs.push(curr.line.ref);
          }
          return acc;
        },
        iniDict
      );
      return Object.values(mergedDests).sort(
        (a, b) => a.line.order - b.line.order
      );
    });
  });

}
